/**
 * Frequency Role - Validate Pillar
 *
 * Validação de valores de frequência.
 * ZERO dependências de outros pilares - totalmente standalone.
 *
 * @example
 * import { validateFrequency, isValidFrequency } from '@attrx/role-morphic/frequency/validate';
 *
 * validateFrequency(100);   // { valid: true, errors: [] }
 * validateFrequency(-5);    // { valid: false, errors: ['Frequency cannot be negative'] }
 * isValidFrequency(100);    // true
 */

// =============================================================================
// TYPES
// =============================================================================

export interface FrequencyValidationConfig {
  /** Valor mínimo permitido (default: 0) */
  min?: number;
  /** Valor máximo permitido */
  max?: number;
  /** Mensagem de erro para valor abaixo do mínimo */
  minError?: string;
  /** Mensagem de erro para valor acima do máximo */
  maxError?: string;
  /** Permitir valores negativos (default: false) */
  allowNegative?: boolean;
}

export interface ValidationResult {
  valid: boolean;
  errors: string[];
}

// =============================================================================
// DEFAULT CONFIG
// =============================================================================

export const FREQUENCY_VALIDATION_CONFIG: FrequencyValidationConfig = {
  min: 0,
  minError: "Frequency cannot be negative",
  allowNegative: false,
};

// =============================================================================
// VALIDATE
// =============================================================================

/**
 * Valida um valor de frequência
 *
 * @param value - Valor a validar
 * @param config - Configuração de validação (opcional)
 * @returns Resultado da validação com lista de erros
 *
 * @example
 * validateFrequency(100);                    // { valid: true, errors: [] }
 * validateFrequency(-5);                     // { valid: false, errors: ['Frequency cannot be negative'] }
 * validateFrequency(-5, { allowNegative: true }); // { valid: true, errors: [] }
 * validateFrequency(1000, { max: 500 });     // { valid: false, errors: ['Value must be at most 500'] }
 */
export function validateFrequency(
  value: unknown,
  config: FrequencyValidationConfig = FREQUENCY_VALIDATION_CONFIG,
): ValidationResult {
  const errors: string[] = [];

  // Must be a number
  if (typeof value !== "number" || !Number.isFinite(value)) {
    return {
      valid: false,
      errors: ["Value must be a finite number"],
    };
  }

  // Merge with defaults
  const effectiveConfig = { ...FREQUENCY_VALIDATION_CONFIG, ...config };

  // Check negative
  if (!effectiveConfig.allowNegative && value < 0) {
    errors.push(effectiveConfig.minError || "Frequency cannot be negative");
  }

  // Check min (if allowNegative is true and min is set)
  if (
    effectiveConfig.allowNegative &&
    effectiveConfig.min !== undefined &&
    value < effectiveConfig.min
  ) {
    errors.push(effectiveConfig.minError || `Value must be at least ${effectiveConfig.min}`);
  }

  // Check max
  if (effectiveConfig.max !== undefined && value > effectiveConfig.max) {
    errors.push(effectiveConfig.maxError || `Value must be at most ${effectiveConfig.max}`);
  }

  return {
    valid: errors.length === 0,
    errors,
  };
}

/**
 * Verifica se valor de frequência é válido (helper booleano)
 *
 * @param value - Valor a validar
 * @param config - Configuração de validação (opcional)
 * @returns true se válido, false caso contrário
 *
 * @example
 * isValidFrequency(100);  // true
 * isValidFrequency(-5);   // false
 */
export function isValidFrequency(
  value: unknown,
  config?: FrequencyValidationConfig,
): boolean {
  return validateFrequency(value, config).valid;
}
